import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Ruler, Calculator, ReceiptText, Loader2, MapPin, User } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import TakeoffCanvas from "@/components/takeoff/TakeoffCanvas";
import TakeoffTour from "@/components/takeoff/TakeoffTour";
import EstimateMatrix from "@/components/EstimateMatrix";
import BillingLedger from "@/components/BillingLedger";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialTab = ["takeoff", "estimate", "billing"].includes(params.get("tab")) ? params.get("tab") : "takeoff";
  const [project, setProject] = useState(null);
  const [takeoff, setTakeoff] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/takeoff`),
      ]);
      setProject(p.data);
      setTakeoff(t.data);
    } catch { navigate("/app"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Auto-launch the guided tour when arrived here via the onboarding "Take the tour" link.
  useEffect(() => {
    if (!loading && params.get("tour") === "1") {
      const t = setTimeout(() => window.dispatchEvent(new Event("tradeoff:start-tour")), 600);
      return () => clearTimeout(t);
    }
  }, [loading, params]);

  const refreshTakeoff = useCallback(async () => {
    const t = await api.get(`/projects/${id}/takeoff`);
    setTakeoff(t.data);
  }, [id]);

  if (loading) return <AppShell><div className="flex items-center justify-center h-96"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div></AppShell>;

  return (
    <AppShell>
      <div className="border-b border-slate-200 bg-white px-6 md:px-8 py-4">
        <button onClick={() => navigate("/app")} className="text-sm text-slate-500 hover:text-blue-600 flex items-center gap-1.5 mb-3" data-testid="back-to-projects">
          <ArrowLeft className="h-4 w-4" /> All projects
        </button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">{project?.name}</h1>
            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
              {project?.client && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {project.client}</span>}
              {project?.address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {project.address}</span>}
            </div>
          </div>
          <span className={`text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded ${project?.status === "active" ? "text-green-700 bg-green-50" : "text-slate-500 bg-slate-100"}`}>
            {project?.status}
          </span>
        </div>
      </div>

      <div className="p-6 md:p-8">
        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v !== "takeoff") refreshTakeoff(); }}>
          <TabsList className="bg-slate-100 w-full sm:w-auto grid grid-cols-3 sm:inline-flex" data-testid="project-tabs">
            <TabsTrigger value="takeoff" data-testid="tab-takeoff"><Ruler className="h-4 w-4 mr-1.5 shrink-0" /> Takeoff</TabsTrigger>
            <TabsTrigger value="estimate" data-testid="tab-estimate"><Calculator className="h-4 w-4 mr-1.5 shrink-0" /> Estimate</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">
              <ReceiptText className="h-4 w-4 mr-1.5 shrink-0" />
              <span className="sm:hidden">Billing</span>
              <span className="hidden sm:inline">Progress billing</span>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="takeoff" className="mt-6"><TakeoffCanvas projectId={id} /></TabsContent>
          <TabsContent value="estimate" className="mt-6"><EstimateMatrix projectId={id} takeoff={takeoff} /></TabsContent>
          <TabsContent value="billing" className="mt-6"><BillingLedger projectId={id} /></TabsContent>
        </Tabs>
      </div>
      <TakeoffTour tab={tab} setTab={setTab} tourDone={user?.takeoff_tour_done} isSample={project?.is_sample} />
    </AppShell>
  );
};

export default ProjectDetail;
